import "../../servicesPages/serviceDetail/serviceMidTab/ServiceMidTab.scss";
import { useEffect, createRef, useState } from "react";
import WebLayout from "../../components/webLayout/WebLayout";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { domain } from "../../utils/apiData";
import HomeCta from "../home/homeCta/HomeCta";
import ScrollSpy from "react-scrollspy-navigation";

import ServiceClients from "../../servicesPages/serviceDetail/servicesClients/ServiceClients";
import ServiceTestimonial from "../../servicesPages/serviceDetail/serviceTestimonial/ServiceTestimonial";
import Faq from "../home/Faq/Faq";
import Tools from "../../servicesPages/serviceDetail/tools/Tools";
import { InView } from "react-intersection-observer";

// Expertise Icons
import { FaHeadset } from "react-icons/fa6";
import { MdMarkEmailRead } from "react-icons/md";
import { BsCalendarDate } from "react-icons/bs";
import { BsFillQuestionOctagonFill } from "react-icons/bs";

//Benifits Icons
import { GiClockwork } from "react-icons/gi";
import { RiCustomerServiceFill } from "react-icons/ri";
import { RiFeedbackFill } from "react-icons/ri";
import { FaCheckCircle } from "react-icons/fa";
import { BiSolidOffer } from "react-icons/bi";
// Tools Icons
import { FaShop } from "react-icons/fa6";
import { MdOutlineRealEstateAgent } from "react-icons/md";
import { IoRestaurantSharp } from "react-icons/io5";
import { MdOutlinePrecisionManufacturing } from "react-icons/md";
import { GiHealthNormal } from "react-icons/gi";
import { BsPiggyBankFill } from "react-icons/bs";
import { GiTeacher } from "react-icons/gi";
import ServiceProcess from "../../servicesPages/serviceDetail/serviceProcess/ServiceProcess";
import StrategiesSec from "../about/strategiesSec/StrategiesSec";
import DataAnaProcess from "./dataAnaProcess/DataAnaProcess";
import CaseStudy from "./caseStudy/CaseStudy";
import AiLanding from "./aiLanding/AiLanding";
import UseCases from "./useCases/UseCases";

import callAudio from "../../assets/audio/aiCall.mp4";
import Comparision from "./comparision/Comparision";

const AiAutomation = () => {
  const location = useLocation();
  const [num, setNum] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const goTo = (secId) => {
    const section = document.getElementById(secId);
    if (section) {
      window.scrollTo({
        top: section.offsetTop,
        behavior: "smooth",
      });
    }
  };

  const expertiseData = [
    {
      id: 0,
      img: <FaHeadset />,
      title: "AI-Powered Communication System",
      text: "Automate incoming and outgoing calls with smart AI that understands how customers feel. Connect with customers instantly and send personalized replies.",
    },
    {
      id: 1,
      img: <MdMarkEmailRead />,
      title: "Smart Email & Text Automation",
      text: "Get AI-written email replies that understand the context. Automatically send follow-ups and schedule messages. Quickly answer questions with smart, friendly responses.",
    },
    {
      id: 2,
      img: <BsCalendarDate />,
      title: "Booking & Scheduling System",
      text: "Easily book and manage appointments with AI. Get automatic reminders and reschedule when needed. Syncs with your calendar and CRM tools.",
    },
    {
      id: 3,
      img: <BsFillQuestionOctagonFill />,
      title: "Internal Query Handling",
      text: "A smart system that organizes and routes internal questions to the right departments based on urgency. Provides automatic answers through a self-help knowledge base.",
    },
  ];

  const faqData = [
    {
      id: 1,
      q: "What is AI Automation Suite and who is it for?",
      a: "AI Automation Suite is an all-in-one platform that uses artificial intelligence to automate communication, scheduling, and internal operations. It’s ideal for businesses of all sizes looking to streamline workflows and improve efficiency.",
    },
    {
      id: 2,
      q: "How does the AI handle customer interactions?",
      a: "Our AI uses natural language processing (NLP) and sentiment analysis to understand context, generate relevant responses, and escalate queries when needed — all while maintaining a human-like tone.",
    },
    {
      id: 3,
      q: "Can it integrate with our existing tools like CRM or calendar apps?",
      a: "Yes! AI Automation Suite offers seamless API integrations with popular CRMs, calendars, help desks, and other enterprise tools for smooth syncing and automation.",
    },
    {
      id: 4,
      q: "Is it secure and compliant with data protection regulations?",
      a: "Absolutely. The platform is built on secure, cloud-native architecture and adheres to industry standards such as GDPR and ISO compliance for data privacy and protection.",
    },
    {
      id: 5,
      q: "How long does it take to set up and go live?",
      a: "Most businesses can get started within a few days. Our onboarding team will help with setup, integration, and customisation based on your specific workflows.",
    },
  ];

  const toolsData = [
    {
      id: 2,
      title: "Retail",
      icon: <FaShop />,
    },
    {
      id: 3,
      title: "Real Estate",
      icon: <MdOutlineRealEstateAgent />,
    },
    {
      id: 4,
      title: "Restaurants",
      icon: <IoRestaurantSharp />,
    },
    {
      id: 5,
      title: "Manufacturing",
      icon: <MdOutlinePrecisionManufacturing />,
    },
    {
      id: 6,
      title: "Healthcare",
      icon: <GiHealthNormal />,
    },
    {
      id: 7,
      title: "Finance",
      icon: <BsPiggyBankFill />,
    },
    {
      id: 8,
      title: "Education",
      icon: <GiTeacher />,
    },
  ];

  const useCases = [
    {
      id: "01",
      title: "Customer Service",
      audio: callAudio,
    },
    {
      id: "02",
      title: "Receptionist",
      audio: callAudio,
    },
    {
      id: "03",
      title: "Answering Service",
      audio: callAudio,
    },
  ];

  const benifits = [
    {
      id: 1,
      icon: <GiClockwork />,
      title: "Smart Workflow",
      text: "",
      list: [
        "Handles inquiries, bookings, and follow-ups automatically",
        "Connects with your tools",
        "Saves your team time",
      ],
    },
    {
      id: 2,
      icon: <RiCustomerServiceFill />,
      title: "Handles Multiple Calls",
      text: "",
      list: [
        "No missed leads or long waits",
        "Prioritizes high-intent leads",
        "Works 24/7",
      ],
    },
    {
      id: 3,
      icon: <BsCalendarDate />,
      title: "Books Meetings & Reservations",
      text: "",
      list: [
        "Schedules meetings or reservations instantly",
        "Sends reminders to avoid no-shows",
        "No back-and-forth or manual work needed",
      ],
    },
    {
      id: 4,
      icon: <RiFeedbackFill />,
      title: "Gets Customer Feedback",
      text: "",
      list: [
        "Asks for ratings after each chat or call",
        "Gives quick insights",
        "Helps you improve fast",
      ],
    },
    {
      id: 5,
      icon: <FaCheckCircle />,
      title: "Follows Up on Bad Ratings",
      text: "",
      list: [
        "Reaches out right away",
        "Fixes problems quickly",
        "Shows you care",
      ],
    },
    {
      id: 6,
      icon: <BiSolidOffer />,
      title: "Sends Smart Offers",
      text: "",
      list: [
        "Sends deals to win back unhappy customers",
        "Turns bad experiences into loyalty",
        "Keeps people coming back",
      ],
    },
  ];

  const processData = [
    {
      id: 0,
      t: "Discovery & Requirements Mapping",
      p: "We begin with a collaborative session to understand your current workflows, communication channels, and pain points across departments.",
    },
    {
      id: 1,
      t: "Custom Solution Design",
      p: "Our team configures AI flows — from voice and chat handling to scheduling and ticketing — based on your exact needs, brand tone, and systems.",
    },
    {
      id: 2,
      t: "We integrate with your existing tools (CRMs, calendars, call systems, etc.) and ensure smooth data sync and API connectivity.",
      p: "Integration & Setup",
    },
    {
      id: 3,
      t: "We run simulations, train the AI on your data and use cases, and prepare your team with clear onboarding materials.",
      p: "Testing & Training",
    },
    {
      id: 4,
      t: "Go Live & Continuous Optimisation",
      p: "After launch, we monitor performance, fine-tune workflows, and provide regular updates and insights to maximise impact.",
    },
  ];

  return (
    <WebLayout>
      <Helmet>
        <title>
          AI Automation by Supagrow | Drive Growth with AI Automation
        </title>
        <meta
          name="description"
          content="Unlock the potential of your data Tech Supagrow’s AI Automation solutions."
        />
        <link rel="canonical" href={`${domain}${location?.pathname}`} />
      </Helmet>

      {/* Landing */}
      <AiLanding />

      <div className="services" id="services">
        <section className="service_mid_tab">
          <div className="content_wrap">
            <div className="md_tab_line">
              <ScrollSpy activeClass="active">
                <div className="service_tab_wraper">
                  <div className="btn_wrap">
                    <button type="button" onClick={() => goTo("secA")}>
                      Expertise
                    </button>

                    <button type="button" onClick={() => goTo("secB")}>
                      Benefits
                    </button>

                    <button type="button" onClick={() => goTo("secC")}>
                      Case&nbsp;Studies
                    </button>

                    <button type="button" onClick={() => goTo("secD")}>
                      Clients
                    </button>

                    <button type="button" onClick={() => goTo("secE")}>
                      Review
                    </button>

                    <button type="button" onClick={() => goTo("secF")}>
                      Industries
                    </button>
                    <button type="button" onClick={() => goTo("secG")}>
                      Process
                    </button>

                    <button type="button" onClick={() => goTo("secH")}>
                      FAQ
                    </button>
                  </div>

                  <a
                    href="#secA"
                    className={num === 1 ? "active" : ""}
                    ref={createRef()}
                  >
                    Expertise
                  </a>
                  <a
                    href="#secB"
                    className={num === 2 ? "active" : ""}
                    ref={createRef()}
                  >
                    Benefits
                  </a>

                  <a href="#secC" ref={createRef()}>
                    Case&nbsp;Studies
                  </a>
                  <a href="#secD" ref={createRef()}>
                    Clients
                  </a>
                  <a href="#secE" ref={createRef()}>
                    Review
                  </a>

                  <a href="#secF" ref={createRef()}>
                    Industries
                  </a>
                  <a href="#secG" ref={createRef()}>
                    Process
                  </a>
                  <a href="#secH" ref={createRef()}>
                    FAQ
                  </a>
                </div>
              </ScrollSpy>
            </div>
          </div>
        </section>

        <div id="secA">
          <InView
            as="div"
            onChange={(inView, entry) => {
              if (inView) {
                setNum(1);
              }
            }}
          >
            <StrategiesSec
              t1="Simplify Your Business with AI"
              para=""
              dataList={expertiseData}
            >
              <ul>
                <li>
                  <p>Streamline communication.</p>
                </li>
                <li>
                  <p>Automate operations.</p>
                </li>
                <li>
                  <p>Boost efficiency.</p>
                </li>
              </ul>
            </StrategiesSec>
          </InView>
        </div>

        <div id="secB">
          <InView
            as="div"
            onChange={(inView, entry) => {
              if (inView) {
                setNum(2);
              }
            }}
          >
            <ServiceProcess data={benifits} title="Benifits of an AI Agent" />
          </InView>
        </div>

        <div id="secC">
          <InView as="div" onChange={(inView, entry) => setNum(3)}>
            <CaseStudy />
          </InView>
        </div>

        <div id="secD">
          <ServiceClients />
        </div>

        <div id="secE">
          <ServiceTestimonial />
        </div>

        <div id="secF">
          <Tools title="Industries We Serve" toolsList={toolsData} />
        </div>

        <div id="secG">
          <DataAnaProcess
            title="Getting started is simple"
            dataList={processData}
          />
        </div>
        <Comparision />
        <div id="secH">
          <Faq faqData={faqData} />
        </div>
      </div>

      <HomeCta title="Automate Your Business Today." />
    </WebLayout>
  );
};

export default AiAutomation;
